import * as pc from 'playcanvas';
import { drawRegionWireframe, regionMatrix } from './region-shape';
import type { EditorAreaBounds } from './area-shape';
import {
    assignSplatObjectSlots,
    createSplatObjectTable,
    drawSplatObjectBounds,
    SPLAT_OBJECT_COPY_PART,
    SPLAT_OBJECT_FLAGS_UNIFORM,
    SPLAT_OBJECT_MODIFIER_KEY,
    SPLAT_OBJECT_SLOT_UNIFORM,
    SPLAT_OBJECT_SLOTS_DEFINE,
    splatObjectFlags,
    splatObjectFlagsStatement,
    splatObjectIdOf,
    splatObjectLaneLayout,
    splatObjectPrelude,
    splatObjectTableUniform,
    writeSplatObjectTable,
    type SplatObjectLaneLayout,
} from './splat-objects';
import { setWorkBufferModifierPart } from './workBufferModifiers';

export interface RegionColorEffect {
    key: string;
    /**
     * Composition order: effects run lowest first, then by key. Default 0. Regions (-1) colour and
     * dim the splats before haze covers them, as focus and annotation layers did.
     */
    order?: number;
    /** Color hook: defines `region_<key>(sceneCenter, color)`. Omit for scale-only effects. */
    glsl?: string;
    wgsl?: string;
    /** Scale hook: defines `region_<key>_scale(sceneCenter, scale)`, run after the base rotation/scale logic. */
    scaleGlsl?: string;
    scaleWgsl?: string;
    /**
     * Center hook: defines `region_<key>_center(sceneCenter, offset)`, adding a scene-space
     * offset to the splat's centre (wind). Runs in the render stage (and the splat shadow
     * casters), every frame, on every splat; see composeRegionCenterShader.
     */
    centerGlsl?: string;
    centerWgsl?: string;
    /**
     * Parameters only the render stage reads (the template / splat materials), never pushed to
     * unified components: a new value on a component re-copies all of its splats, so values
     * that change every frame (the wind clock) belong here.
     */
    renderUniforms?: Record<string, Float32Array>;
    /** Shapes the editor outlines while the effect is on (sphere / box wireframes, outline polylines), each in its own colour when it has one. */
    bounds: EditorAreaBounds[];
    bypassFog?: boolean;
    /** Material parameters the effect drives at runtime (slider-style values that must not recompile). */
    uniforms?: Record<string, Float32Array>;
    /**
     * "Splat object" selections (splat-objects.ts): set i is a list of object ids, and the
     * effect's shader calls `splatObjectLaneCall(key, i)` for 1.0 when the splat belongs to one
     * of them. The composer turns the sets into per-component flags; which objects are in a set
     * can change without recompiling (only the number of sets is in the shader), though like any
     * `set` it re-copies every unified splat once (each component's `uRegionTransform` is re-set).
     */
    objectSets?: ReadonlyArray<ReadonlyArray<string>>;
}
const EMPTY_GLSL = `void modifySplatCenter(inout vec3 center) {}\nvoid modifySplatRotationScale(vec3 a, vec3 b, inout vec4 r, inout vec3 s) {}\nvoid modifySplatColor(vec3 center, inout vec4 color) {}`;
const EMPTY_WGSL = `fn modifySplatCenter(center: ptr<function, vec3f>) {}\nfn modifySplatRotationScale(a: vec3f, b: vec3f, r: ptr<function, vec4f>, s: ptr<function, vec3f>) {}\nfn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}`;
/** Wrap the original function, preserving its early returns and all existing effects. */
export function composeRegionColorShader(base: string | undefined, effects: RegionColorEffect[], lang: 'glsl' | 'wgsl'): string {
    return composeRegionCenterShader(composeRegionColorAndScaleShader(base, effects, lang), effects, lang);
}
function composeRegionColorAndScaleShader(
    base: string | undefined,
    effects: RegionColorEffect[],
    lang: 'glsl' | 'wgsl',
): string {
    const source = base || (lang === 'glsl' ? EMPTY_GLSL : EMPTY_WGSL);
    const colorEffects = effects.filter((e) => (lang === 'glsl' ? e.glsl : e.wgsl) !== undefined);
    const scaleEffects = effects.filter((e) => (lang === 'glsl' ? e.scaleGlsl : e.scaleWgsl) !== undefined);
    let renamed = source.replace(/\bmodifySplatColor\s*\(/, 'regionBaseColor(');
    // Only take over the rotation/scale hook when an effect needs it, so pure color
    // effects leave that part of the base chunk byte-for-byte unchanged.
    if (scaleEffects.length) renamed = renamed.replace(/\bmodifySplatRotationScale\s*\(/, 'regionBaseRotationScale(');
    const snippets = [
        ...colorEffects.map((e) => (lang === 'glsl' ? e.glsl : e.wgsl)),
        ...scaleEffects.map((e) => (lang === 'glsl' ? e.scaleGlsl : e.scaleWgsl)),
    ].join('\n');
    // Splat object selections (splat-objects.ts): the per-splat flags lookup goes before the
    // effects that call it, and the centre hook fills the flags from the centre it is given (its
    // low bits carry the object's slot) before anything moves it.
    const layout = splatObjectLaneLayout(effects);
    const prelude = splatObjectPrelude(layout, lang);
    if (layout.length) renamed = renamed.replace(/\bmodifySplatCenter\s*\(/, 'regionBaseCenter(');
    const calls = colorEffects.map((e) => `region_${e.key}(sceneCenter, color);`).join('\n');
    const scaleCalls = scaleEffects.map((e) => `region_${e.key}_scale(sceneCenter, scale);`).join('\n');
    const wrapper =
        lang === 'glsl'
            ? `uniform mat4 uRegionTransform;\nvoid modifySplatColor(vec3 center, inout vec4 color) { regionBaseColor(center, color); vec3 sceneCenter = (uRegionTransform * vec4(center, 1.0)).xyz; ${calls} }`
            : `uniform uRegionTransform: mat4x4f;\nfn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) { regionBaseColor(center, color); let sceneCenter = (uniform.uRegionTransform * vec4f(center, 1.0)).xyz; ${calls} }`;
    const scaleWrapper = !scaleEffects.length
        ? ''
        : lang === 'glsl'
          ? `\nvoid modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) { regionBaseRotationScale(originalCenter, modifiedCenter, rotation, scale); vec3 sceneCenter = (uRegionTransform * vec4(modifiedCenter, 1.0)).xyz; ${scaleCalls} }`
          : `\nfn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) { regionBaseRotationScale(originalCenter, modifiedCenter, rotation, scale); let sceneCenter = (uniform.uRegionTransform * vec4f(modifiedCenter, 1.0)).xyz; ${scaleCalls} }`;
    const centerWrapper = !layout.length
        ? ''
        : lang === 'glsl'
          ? `\nvoid modifySplatCenter(inout vec3 center) { ${splatObjectFlagsStatement('glsl')} regionBaseCenter(center); }`
          : `\nfn modifySplatCenter(center: ptr<function, vec3f>) { ${splatObjectFlagsStatement('wgsl')} regionBaseCenter(center); }`;
    return `${renamed}\n${prelude}${snippets}\n${wrapper}${scaleWrapper}${centerWrapper}`;
}
/**
 * The centre hooks (wind) on top of the composed source: whatever `modifySplatCenter` it has
 * (the base chunk's, or a wrapper composed before this stage) runs first and is left as it is,
 * then the effects' scene-space offset moves the centre. Anything that reads the centre as given
 * (e.g. bits a copy stage stored in it) therefore sees it unmoved. Relighting's point-light caster
 * slide (rl_casterCenter) runs before the offset, so a swaying splat's point-light shadow depth is
 * off by the sway along the light's ray (centimetres): its position in the shadow follows it.
 * Only taken over when a centre effect is on, so other effects leave the hook byte-for-byte unchanged.
 */
function composeRegionCenterShader(composed: string, effects: RegionColorEffect[], lang: 'glsl' | 'wgsl'): string {
    const glsl = lang === 'glsl';
    const centerEffects = effects.filter((e) => (glsl ? e.centerGlsl : e.centerWgsl) !== undefined);
    if (!centerEffects.length) return composed;
    const hasCenter = /\bmodifySplatCenter\s*\(/.test(composed);
    const inner = hasCenter
        ? composed.replace(/\bmodifySplatCenter\s*\(/, 'regionCenterBeforeMotion(')
        : `${composed}\n${glsl ? 'void regionCenterBeforeMotion(inout vec3 center) {}' : 'fn regionCenterBeforeMotion(center: ptr<function, vec3f>) {}'}`;
    const snippets = centerEffects.map((e) => (glsl ? e.centerGlsl : e.centerWgsl)).join('\n');
    const calls = centerEffects.map((e) => (glsl ? `region_${e.key}_center(sceneCenter, regionOffset);` : `region_${e.key}_center(sceneCenter, &regionOffset);`)).join(' ');
    // The offset is scene-space; uRegionTransformInv takes it back to the centre's own space.
    const wrapper = glsl
        ? `uniform mat4 uRegionTransformInv;\nvoid modifySplatCenter(inout vec3 center) { regionCenterBeforeMotion(center); vec3 sceneCenter = (uRegionTransform * vec4(center, 1.0)).xyz; vec3 regionOffset = vec3(0.0); ${calls} center += (uRegionTransformInv * vec4(regionOffset, 0.0)).xyz; }`
        : `uniform uRegionTransformInv: mat4x4f;\nfn modifySplatCenter(center: ptr<function, vec3f>) { regionCenterBeforeMotion(center); let sceneCenter = (uniform.uRegionTransform * vec4f(*center, 1.0)).xyz; var regionOffset = vec3f(0.0); ${calls} *center = *center + (uniform.uRegionTransformInv * vec4f(regionOffset, 0.0)).xyz; }`;
    return `${inner}\n${snippets}\n${wrapper}`;
}
type MaterialState = { base: string | undefined; applied: string; revision: number };
/** Scratch for inverting a material's region transform (see SplatRegionEffects.inverseOf). */
const _inverse = new pc.Mat4();
const instances = new WeakMap<pc.Application, SplatRegionEffects>();
/** A shared owner prevents independently authored effects from replacing one another. */
export function getSplatRegionEffects(app: pc.Application, scene: pc.Entity, editor: boolean): SplatRegionEffects {
    let instance = instances.get(app);
    if (!instance) {
        instance = new SplatRegionEffects(app, scene, editor);
        instances.set(app, instance);
    }
    return instance;
}
class SplatRegionEffects {
    private effects = new Map<string, RegionColorEffect>();
    private materials = new Map<pc.Material, MaterialState>();
    private fogDefines = new Map<pc.Material, string | boolean | undefined>();
    private componentMatrices = new WeakMap<pc.GSplatComponent, { matrix: Float32Array; revision: number }>();
    /** Each material's uRegionTransformInv and the transform it was inverted from. */
    private inverses = new WeakMap<pc.Material, { source: Float32Array; inverse: Float32Array }>();
    private revision = 0;
    private listening = false;
    /** Splat object selections: the slot table, its last value, components carrying the copy part, flags cache. */
    private objectSlots = false;
    private objectTable = createSplatObjectTable();
    private objectFlagsKey = '';
    private slotted = new Set<pc.GSplatComponent>();
    private componentFlags = new WeakMap<pc.Material, number>();
    constructor(
        private app: pc.Application,
        private scene: pc.Entity,
        private editor: boolean,
    ) {
        app.once('destroy', this.destroy, this);
    }
    set(key: string, effect: RegionColorEffect | undefined): void {
        if (effect) this.effects.set(key, effect);
        else this.effects.delete(key);
        this.revision++;
        if (this.effects.size && !this.listening) {
            this.app.on('prerender', this.sync, this);
            this.listening = true;
        }
        // Changes must render even with the viewer's idle scheduler enabled.
        this.app.renderNextFrame = true;
        this.sync();
        if (!this.effects.size && this.listening) {
            this.app.off('prerender', this.sync, this);
            this.listening = false;
        }
    }
    private sync(): void {
        const lang = this.app.graphicsDevice.isWebGPU ? 'wgsl' : 'glsl';
        const active = [...this.effects.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.key.localeCompare(b.key));
        if (!active.length) {
            for (const [material, state] of this.materials) {
                const chunks = material.getShaderChunks(lang);
                if (chunks.get('gsplatModifyVS') === state.applied) {
                    if (state.base === undefined) chunks.delete('gsplatModifyVS');
                    else chunks.set('gsplatModifyVS', state.base);
                    material.update();
                }
            }
            for (const [material, value] of this.fogDefines) {
                material.setDefine('GSPLAT_NO_FOG', value);
                material.update();
            }
            this.fogDefines.clear();
            this.setObjectSlots(false);
            // Removing the modifier must also invalidate unified color work buffers.
            for (const component of this.scene.findComponents('gsplat') as pc.GSplatComponent[]) {
                if (component.asset != null)
                    component.setParameter('uRegionTransform', new Float32Array(new pc.Mat4().data));
            }
            this.materials.clear();
            return;
        }
        const inverseScene = new pc.Mat4().copy(this.scene.getWorldTransform()).invert();
        const targets = new Map<pc.Material, pc.Mat4>();
        const template = this.app.scene.gsplat?.material;
        if (template) targets.set(template, inverseScene);
        const components = this.scene.findComponents('gsplat') as pc.GSplatComponent[];
        for (const component of components) {
            // Procedural weather uses its own shader and is not an authored scan.
            if (component.asset == null) continue;
            const matrix = component.unified
                ? inverseScene
                : new pc.Mat4().mul2(inverseScene, component.entity.getWorldTransform());
            const previous = this.componentMatrices.get(component);
            if (
                !previous ||
                previous.revision !== this.revision ||
                matrix.data.some((v, i) => v !== previous.matrix[i])
            ) {
                const data = new Float32Array(matrix.data);
                component.setParameter('uRegionTransform', data); // invalidates cached unified work buffers
                this.componentMatrices.set(component, { matrix: data, revision: this.revision });
            }
            this.applyUniforms(component, active);
            if (!component.unified && component.material) targets.set(component.material, matrix);
        }
        // Splat object selections: the template reads slots from the centre bits (unified splats).
        const layout = splatObjectLaneLayout(active);
        this.setObjectSlots(layout.length > 0);
        for (const [material, matrix] of targets) {
            const bypassFog = active.some((effect) => effect.bypassFog);
            if (bypassFog) {
                if (!this.fogDefines.has(material))
                    this.fogDefines.set(material, material.defines.get('GSPLAT_NO_FOG'));
                material.setDefine('GSPLAT_NO_FOG', true);
            } else if (this.fogDefines.has(material)) {
                material.setDefine('GSPLAT_NO_FOG', this.fogDefines.get(material));
                this.fogDefines.delete(material);
            }
            const chunks = material.getShaderChunks(lang);
            const current = chunks.get('gsplatModifyVS');
            let state = this.materials.get(material);
            if (!state || current !== state.applied) state = { base: current, applied: '', revision: -1 };
            if (state.revision !== this.revision) {
                state.applied = composeRegionColorShader(state.base, active, lang);
                state.revision = this.revision;
                chunks.set('gsplatModifyVS', state.applied);
                material.update();
            }
            material.setParameter('uRegionTransform', matrix.data);
            material.setParameter('uRegionTransformInv', this.inverseOf(material, matrix));
            this.applyUniforms(material, active);
            this.materials.set(material, state);
        }
        if (layout.length) this.syncObjectFlags(layout, targets, template);
        // Release references to disposed swap materials. Never overwrite an external replacement.
        for (const material of this.materials.keys()) if (!targets.has(material)) {
            this.materials.delete(material);
            this.fogDefines.delete(material);
        }
        if (this.editor) for (const effect of active) for (const shape of effect.bounds) this.drawBounds(shape);
    }
    /**
     * Switch the template's slot read-back on or off. Off also takes the copy-stage part off every
     * component that has it (each re-copies once, back to its plain centres).
     */
    private setObjectSlots(on: boolean): void {
        const template = this.app.scene.gsplat?.material;
        if (on === this.objectSlots || !template) return;
        this.objectSlots = on;
        template.setDefine(SPLAT_OBJECT_SLOTS_DEFINE, on);
        template.update();
        this.objectFlagsKey = '';
        if (!on) {
            for (const component of this.slotted) setWorkBufferModifierPart(component, SPLAT_OBJECT_MODIFIER_KEY, null);
            this.slotted.clear();
        }
    }
    /**
     * Slots and flags for the splat object selections. Every unified gsplat in the app gets the
     * copy-stage part (its centres' low bits must hold a slot, 0 when it is no object: the shader
     * reads every unified splat's bits). Registered objects' components get slots 1..63, selected
     * ones first (assignSplatObjectSlots); the template's table maps each slot to its flags. A
     * non-unified component's own material gets its flags.
     */
    private syncObjectFlags(layout: SplatObjectLaneLayout[], targets: Map<pc.Material, pc.Mat4>, template: pc.Material | undefined): void {
        const all = this.app.root.findComponents('gsplat') as pc.GSplatComponent[];
        const objects: Array<{ component: pc.GSplatComponent; flags: number }> = [];
        const present = new Set<pc.GSplatComponent>();
        for (const component of all) {
            const id = splatObjectIdOf(component.entity);
            const flags = splatObjectFlags(id, layout);
            if (component.unified) {
                present.add(component);
                if (id) objects.push({ component, flags });
            } else if (component.material && targets.has(component.material) && this.componentFlags.get(component.material) !== flags) {
                component.material.setParameter(SPLAT_OBJECT_FLAGS_UNIFORM, new Float32Array([flags, 0, 0, 0]));
                this.componentFlags.set(component.material, flags);
            }
        }
        const slots = assignSplatObjectSlots(objects);
        const flagsBySlot: number[] = [0];
        for (const component of present) {
            const slot = slots.get(component) ?? 0;
            if (!this.slotted.has(component)) {
                setWorkBufferModifierPart(component, SPLAT_OBJECT_MODIFIER_KEY, SPLAT_OBJECT_COPY_PART);
                this.slotted.add(component);
            }
            // A changed slot re-copies that component's splats (setParameter marks it dirty).
            if (component.getParameter(SPLAT_OBJECT_SLOT_UNIFORM) !== slot) component.setParameter(SPLAT_OBJECT_SLOT_UNIFORM, slot);
        }
        for (const o of objects) {
            const slot = slots.get(o.component);
            if (slot) flagsBySlot[slot] = o.flags;
        }
        for (const component of this.slotted) if (!present.has(component)) this.slotted.delete(component);
        if (!template) return;
        const key = flagsBySlot.join(',');
        if (key === this.objectFlagsKey) return;
        this.objectFlagsKey = key;
        writeSplatObjectTable(this.objectTable, flagsBySlot);
        this.objectTable.forEach((value, i) => template.setParameter(splatObjectTableUniform(i), new Float32Array(value)));
        template.setParameter(SPLAT_OBJECT_FLAGS_UNIFORM, new Float32Array(4));
        // The unified renderers re-copy template parameters only when it is flagged dirty.
        template.update();
        this.app.renderNextFrame = true;
    }
    /**
     * A material's uRegionTransformInv: inverted (in a scratch matrix) only when its transform
     * changed, not every frame. A change gets a new array, as before: the unified renderers hold
     * the template's parameter arrays by reference until it is next updated.
     */
    private inverseOf(material: pc.Material, matrix: pc.Mat4): Float32Array {
        const cached = this.inverses.get(material);
        if (cached) {
            let same = true;
            for (let i = 0; i < 16 && same; i++) same = cached.source[i] === matrix.data[i];
            if (same) return cached.inverse;
        }
        const inverse = new Float32Array(_inverse.copy(matrix).invert().data);
        this.inverses.set(material, { source: new Float32Array(matrix.data), inverse });
        return inverse;
    }
    private uniformCache = new WeakMap<object, Map<string, Float32Array>>();
    /**
     * Push effect-driven parameters only when their values changed: a fresh value on a
     * unified component invalidates its cached work buffer, so a per-frame write would
     * re-evaluate every splat every frame even while nothing moves.
     */
    private applyUniforms(target: pc.Material | pc.GSplatComponent, active: RegionColorEffect[]): void {
        let cache = this.uniformCache.get(target);
        if (!cache) {
            cache = new Map();
            this.uniformCache.set(target, cache);
        }
        let changed = false;
        // A material is the render stage: it also takes the effects' render-only parameters.
        const renderStage = target instanceof pc.Material;
        for (const effect of active) {
            const sets = renderStage ? [effect.uniforms, effect.renderUniforms] : [effect.uniforms];
            for (const [name, value] of sets.flatMap((u) => (u ? Object.entries(u) : []))) {
                const previous = cache.get(name);
                if (previous && previous.length === value.length && previous.every((v, i) => v === value[i])) continue;
                const copy = new Float32Array(value);
                target.setParameter(name, copy);
                cache.set(name, copy);
                changed = true;
            }
        }
        // The unified renderers keep their own copy of the template material and only
        // re-copy its parameters when it is flagged dirty. With no chunk or define changes
        // update() does exactly that and nothing else (no shader variant reset).
        if (changed && target instanceof pc.Material) target.update();
    }
    private drawBounds(shape: EditorAreaBounds): void {
        const color = shape.color ? new pc.Color(shape.color[0], shape.color[1], shape.color[2]) : new pc.Color(0.25, 0.85, 1);
        if (shape.shape === 'splats') {
            drawSplatObjectBounds(this.app, this.scene, shape.ids, color);
            return;
        }
        if (shape.shape === 'outline') {
            // Outline points are runtime scene coordinates: through the scene root, closed.
            const m = this.scene.getWorldTransform();
            const points = shape.points.map((p) => m.transformPoint(new pc.Vec3(p.x, p.y, p.z)));
            if (points.length >= 2) for (let i = 0; i < points.length; i++) this.app.drawLine(points[i], points[(i + 1) % points.length], color, false);
            return;
        }
        const matrix = new pc.Mat4().mul2(this.scene.getWorldTransform(), regionMatrix(shape));
        drawRegionWireframe(this.app, matrix, shape.shape, color);
    }
    destroy(): void {
        this.app.off('prerender', this.sync, this);
        this.effects.clear();
        this.materials.clear();
        this.fogDefines.clear();
        instances.delete(this.app);
    }
}
